import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useSEO, SITE_URL } from '../../../hooks/useSEO';

const DataProtectionPage = () => {
  useSEO({
    title: 'Data Protection & Loss Prevention for Mortgage Companies | Mortgage Moat',
    description: 'Safeguard sensitive financial data with encryption, DLP, access controls, and backup strategies. Learn how to protect customer data and meet GLBA compliance requirements.',
    keywords: 'data protection mortgage, data loss prevention, financial data security, encryption compliance, GLBA data protection',
    canonical: '/knowledge-center/data-protection',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Data Protection & Loss Prevention for Mortgage Companies',
        description: 'Safeguarding sensitive financial data through encryption, access controls, data loss prevention strategies, and comprehensive backup solutions.',
        url: `${SITE_URL}/knowledge-center/data-protection`,
        publisher: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        author: { '@type': 'Organization', name: 'Mortgage Moat' },
        dateModified: '2025-07-14',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Knowledge Center', item: `${SITE_URL}/knowledge-center/data-protection` },
          { '@type': 'ListItem', position: 3, name: 'Data Protection', item: `${SITE_URL}/knowledge-center/data-protection` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-green-50 via-white to-gray-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-shield-line"></i>
                Security Knowledge Center
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Data Protection & <span className="text-green-600">Loss Prevention</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Safeguarding sensitive financial data through encryption, access controls, data loss prevention strategies, and comprehensive backup solutions
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button variant="primary" size="lg" className="whitespace-nowrap">
                  <i className="ri-lock-2-line mr-2"></i>
                  Protect Your Data
                </Button>
                <Button variant="outline" size="lg" className="whitespace-nowrap">
                  <i className="ri-search-line mr-2"></i>
                  Data Security Audit
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-green-600 mb-2">$165</div>
                <p className="text-gray-700 font-semibold">Cost Per Lost Record</p>
                <p className="text-sm text-gray-600 mt-2">Average in financial services</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-green-600 mb-2">287 Days</div>
                <p className="text-gray-700 font-semibold">Average Time to Identify Breach</p>
                <p className="text-sm text-gray-600 mt-2">IBM Cost of Data Breach Report</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-green-600 mb-2">60%</div>
                <p className="text-gray-700 font-semibold">Small Businesses Close After Breach</p>
                <p className="text-sm text-gray-600 mt-2">Within 6 months of incident</p>
              </div>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-green-50 border-l-4 border-green-600 p-6">
                <div className="flex items-start gap-4">
                  <i className="ri-database-2-line text-3xl text-green-600 flex-shrink-0"></i>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Your Data Is Your Most Valuable Asset</h3>
                    <p className="text-gray-700">
                      Mortgage companies handle some of the most sensitive personal and financial information—Social Security numbers, bank statements, tax returns, and credit reports. <strong>Protecting this data</strong> isn't just about compliance; it's about maintaining customer trust and business viability.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Data Loss Scenarios */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Common Data Loss & Exposure Scenarios</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Understanding how sensitive data can be compromised or lost
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-green-600">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-hacker-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Cyberattacks & Breaches</h3>
                <p className="text-gray-700 text-sm mb-4">
                  External attackers gaining unauthorized access to databases and file systems containing customer information.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Database breaches</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Ransomware encryption</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-green-600 flex-shrink-0 mt-0.5"></i>
                    <span>Data exfiltration</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-red-600">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-user-unfollow-line text-3xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Insider Threats</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Employees or contractors intentionally or accidentally exposing or stealing sensitive data.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-0.5"></i>
                    <span>Data theft by employees</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-0.5"></i>
                    <span>Accidental email exposure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-red-600 flex-shrink-0 mt-0.5"></i>
                    <span>Unauthorized data downloads</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-orange-600">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-device-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Lost or Stolen Devices</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Laptops, phones, USB drives, or backup media containing unencrypted sensitive information.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-0.5"></i>
                    <span>Unencrypted laptops</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-0.5"></i>
                    <span>Mobile device theft</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-orange-600 flex-shrink-0 mt-0.5"></i>
                    <span>Lost backup drives</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-blue-600">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-settings-3-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Misconfigurations</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Improperly configured systems, databases, or cloud storage exposing data to the internet.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                    <span>Public cloud buckets</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                    <span>Open database ports</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-blue-600 flex-shrink-0 mt-0.5"></i>
                    <span>Weak access controls</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-purple-600">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-hard-drive-2-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Hardware Failures</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Server crashes, disk failures, or natural disasters destroying data without proper backups.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                    <span>Server hardware failure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                    <span>Storage corruption</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-purple-600 flex-shrink-0 mt-0.5"></i>
                    <span>Natural disasters</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-yellow-600">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-delete-bin-line text-3xl text-yellow-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Improper Disposal</h3>
                <p className="text-gray-700 text-sm mb-4">
                  Failure to properly destroy data on decommissioned equipment or disposed documents.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 flex-shrink-0 mt-0.5"></i>
                    <span>Unwiped hard drives</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 flex-shrink-0 mt-0.5"></i>
                    <span>Discarded documents</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 flex-shrink-0 mt-0.5"></i>
                    <span>Recycled equipment</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Data Protection Strategies */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Data Protection Framework</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Multi-layered approach to securing sensitive information throughout its lifecycle
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-12">
              <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-8 border-l-4 border-green-600">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-lock-2-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Encryption</h3>
                    <p className="text-gray-600 text-sm">Protect data at rest and in transit</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Data at Rest</p>
                      <p className="text-xs text-gray-600">Full disk encryption (AES-256) on all devices and servers</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Data in Transit</p>
                      <p className="text-xs text-gray-600">TLS 1.3 for all network communications and file transfers</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Database Encryption</p>
                      <p className="text-xs text-gray-600">Transparent data encryption for sensitive database fields</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Email Encryption</p>
                      <p className="text-xs text-gray-600">S/MIME or PGP for sensitive email communications</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-8 border-l-4 border-green-600">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Access Controls</h3>
                    <p className="text-gray-600 text-sm">Limit who can view and modify data</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Role-Based Access</p>
                      <p className="text-xs text-gray-600">Permissions based on job function and need-to-know</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Multi-Factor Authentication</p>
                      <p className="text-xs text-gray-600">Required for accessing sensitive data systems</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Privileged Access Management</p>
                      <p className="text-xs text-gray-600">Strict controls on administrative accounts</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Access Reviews</p>
                      <p className="text-xs text-gray-600">Quarterly audits of user permissions and access rights</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-8 border-l-4 border-green-600">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-eye-off-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Data Loss Prevention (DLP)</h3>
                    <p className="text-gray-600 text-sm">Prevent unauthorized data exfiltration</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Email DLP</p>
                      <p className="text-xs text-gray-600">Scan outbound emails for sensitive data patterns</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Endpoint DLP</p>
                      <p className="text-xs text-gray-600">Block unauthorized file transfers to USB or cloud</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Network DLP</p>
                      <p className="text-xs text-gray-600">Monitor and control data leaving your network</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Cloud DLP</p>
                      <p className="text-xs text-gray-600">Protect data in SaaS applications and cloud storage</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-8 border-l-4 border-green-600">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-database-2-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Backup & Recovery</h3>
                    <p className="text-gray-600 text-sm">Ensure data availability and resilience</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">3-2-1 Backup Rule</p>
                      <p className="text-xs text-gray-600">3 copies, 2 different media, 1 offsite location</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Immutable Backups</p>
                      <p className="text-xs text-gray-600">Ransomware-proof backups that cannot be encrypted</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Regular Testing</p>
                      <p className="text-xs text-gray-600">Monthly restoration tests to verify backup integrity</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-green-600 flex-shrink-0 mt-1"></i>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Automated Backups</p>
                      <p className="text-xs text-gray-600">Daily incremental, weekly full backups</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Data Classification */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Data Classification Framework</h2>
                <p className="text-lg text-gray-600">
                  Categorize data to apply appropriate protection levels
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-white rounded-lg p-6 border-l-4 border-red-600 shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-2xl text-red-600"></i>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Highly Confidential</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Data requiring maximum protection: SSNs, account numbers, passwords, credit reports
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full">Encryption Required</span>
                        <span className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full">MFA Access</span>
                        <span className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full">Audit Logging</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border-l-4 border-orange-600 shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-line text-2xl text-orange-600"></i>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Confidential</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Internal business data: loan applications, financial statements, employee records
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded-full">Access Controls</span>
                        <span className="text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded-full">Secure Transmission</span>
                        <span className="text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded-full">Need-to-Know</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border-l-4 border-blue-600 shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-line text-2xl text-blue-600"></i>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Internal Use</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        General business information: policies, procedures, internal communications
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Employee Access</span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Standard Protection</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border-l-4 border-green-600 shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-2xl text-green-600"></i>
                    </div>
                    <div className="flex-grow">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Public</h4>
                      <p className="text-sm text-gray-700 mb-3">
                        Information intended for public consumption: marketing materials, press releases
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">No Restrictions</span>
                        <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">Public Access</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Compliance Requirements */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-green-600 to-blue-600 rounded-xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Data Protection Compliance Requirements</h3>
                <p className="text-green-100 mb-6">
                  Financial services firms must comply with multiple data protection regulations:
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold mb-3">Federal Regulations</h4>
                    <ul className="space-y-2 text-sm text-green-100">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>GLBA Safeguards Rule data security</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>FCRA consumer data protection</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>ECOA fair lending data requirements</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold mb-3">State & Industry Standards</h4>
                    <ul className="space-y-2 text-sm text-green-100">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>State breach notification laws</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>CCPA/CPRA consumer privacy rights</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>NIST Cybersecurity Framework</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Protect Your Most Valuable Asset
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
              Our comprehensive data protection solutions combine encryption, access controls, DLP, and backup strategies to safeguard your sensitive financial data while maintaining regulatory compliance.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" size="lg" className="bg-white text-green-600 hover:bg-gray-100 border-white whitespace-nowrap">
                <i className="ri-search-line mr-2"></i>
                Data Security Assessment
              </Button>
              <Button variant="primary" size="lg" className="bg-white text-green-600 hover:bg-gray-100 whitespace-nowrap">
                <i className="ri-shield-check-line mr-2"></i>
                Deploy Data Protection
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default DataProtectionPage;